/**
 * Página de Resumen por Categoría
 * Muestra gastos/ingresos agrupados con drill-down
 */
import React, { useState, useEffect, useCallback } from 'react';
import FinAppLayout from '../../components/FinAppLayout';
import { reportesApi, categoriasApi } from '../../lib/finanzas_api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  PieChart, Loader2, ChevronDown, ChevronRight, Calendar,
  TrendingUp, TrendingDown, ArrowUpCircle, ArrowDownCircle
} from 'lucide-react';

const FinanzasResumenPage = () => {
  const [datos, setDatos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState([]);
  
  // Filtros
  const [filtros, setFiltros] = useState({
    tipo: 'Gasto',
    fecha_desde: '',
    fecha_hasta: ''
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await reportesApi.porCategoria({
        tipo: filtros.tipo !== 'all' ? filtros.tipo : undefined,
        fecha_desde: filtros.fecha_desde || undefined,
        fecha_hasta: filtros.fecha_hasta || undefined,
      });
      setDatos(res.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleExpand = (id) => {
    setExpandedIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const formatMoney = (amount) => {
    return `$${amount?.toLocaleString('es-CL', { minimumFractionDigits: 0 })}`;
  };

  const total = datos.reduce((sum, d) => sum + (d.total || 0), 0);
  
  // Calcular porcentaje para cada categoría
  const datosConPorcentaje = datos.map(d => ({
    ...d,
    porcentaje: total > 0 ? ((d.total / total) * 100).toFixed(1) : 0
  }));

  // Colores predefinidos para el gráfico de barras
  const getBarColor = (index) => {
    const colors = [
      'bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-red-500', 
      'bg-purple-500', 'bg-pink-500', 'bg-cyan-500', 'bg-orange-500'
    ];
    return colors[index % colors.length];
  };

  // Preset de fechas
  const setPreset = (preset) => {
    const hoy = new Date();
    let desde = new Date();
    
    switch (preset) {
      case 'mes':
        desde = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
        break;
      case 'trimestre':
        desde = new Date(hoy.getFullYear(), hoy.getMonth() - 2, 1);
        break;
      case 'año':
        desde = new Date(hoy.getFullYear(), 0, 1);
        break;
      default:
        return;
    }
    
    setFiltros({
      ...filtros,
      fecha_desde: desde.toISOString().split('T')[0],
      fecha_hasta: hoy.toISOString().split('T')[0]
    });
  };

  return (
    <FinAppLayout>
      <div className="space-y-4" data-testid="finanzas-resumen">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <PieChart className="h-6 w-6 text-amber-600" />
            <div>
              <h1 className="text-xl font-bold text-slate-800">Resumen por Categoría</h1>
              <p className="text-xs text-slate-500">Análisis de gastos e ingresos por categoría</p>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Select value={filtros.tipo} onValueChange={(v) => setFiltros({ ...filtros, tipo: v })}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="Ingreso">Ingresos</SelectItem>
                <SelectItem value="Gasto">Gastos</SelectItem>
              </SelectContent>
            </Select>
            
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-slate-400" />
              <Input
                type="date"
                value={filtros.fecha_desde}
                onChange={(e) => setFiltros({ ...filtros, fecha_desde: e.target.value })}
                className="w-36"
              />
              <span className="text-slate-400">-</span>
              <Input
                type="date"
                value={filtros.fecha_hasta}
                onChange={(e) => setFiltros({ ...filtros, fecha_hasta: e.target.value })}
                className="w-36"
              />
            </div>
            
            <div className="flex gap-1">
              <Button variant="outline" size="sm" onClick={() => setPreset('mes')}>Este mes</Button>
              <Button variant="outline" size="sm" onClick={() => setPreset('trimestre')}>Trimestre</Button>
              <Button variant="outline" size="sm" onClick={() => setPreset('año')}>Este año</Button>
            </div>
          </div>
        </div>

        {/* Contenido */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
          </div>
        ) : datos.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
            <PieChart className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No hay datos para el período seleccionado</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Resumen Total */}
            <div className="lg:col-span-1">
              <div className={`rounded-xl p-6 text-white ${
                filtros.tipo === 'Ingreso' ? 'bg-gradient-to-br from-emerald-500 to-emerald-600' :
                filtros.tipo === 'Gasto' ? 'bg-gradient-to-br from-red-500 to-red-600' :
                'bg-gradient-to-br from-slate-600 to-slate-700'
              }`}>
                <div className="flex items-center gap-2 mb-4">
                  {filtros.tipo === 'Ingreso' ? <TrendingUp className="h-5 w-5" /> : 
                   filtros.tipo === 'Gasto' ? <TrendingDown className="h-5 w-5" /> :
                   <PieChart className="h-5 w-5" />}
                  <span className="font-medium">
                    {filtros.tipo === 'Ingreso' ? 'Total Ingresos' : 
                     filtros.tipo === 'Gasto' ? 'Total Gastos' : 'Total'}
                  </span>
                </div>
                <p className="text-3xl font-bold">{formatMoney(total)}</p>
                <p className="text-sm opacity-80 mt-2">{datos.length} categorías</p>
              </div>
              
              {/* Mini gráfico de barras */}
              <div className="bg-white border border-slate-200 rounded-lg mt-4 p-4">
                <h3 className="font-semibold text-slate-700 mb-3 text-sm">Distribución</h3>
                <div className="space-y-2">
                  {datosConPorcentaje.slice(0, 5).map((item, idx) => (
                    <div key={item.categoria_id || idx}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-slate-600 truncate max-w-[150px]">
                          {item.categoria_nombre || item.categoria?.nombre || 'Sin categoría'}
                        </span>
                        <span className="text-slate-500">{item.porcentaje}%</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${getBarColor(idx)} rounded-full transition-all`}
                          style={{ width: `${item.porcentaje}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Tabla de detalles */}
            <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
                <h2 className="font-semibold text-slate-800">Detalle por Categoría</h2>
              </div>
              <div className="divide-y divide-slate-100 max-h-[500px] overflow-y-auto">
                {datosConPorcentaje.map((item, idx) => {
                  const isExpanded = expandedIds.includes(item.categoria_id);
                  return (
                    <div key={item.categoria_id || idx}>
                      <div 
                        className="px-4 py-3 flex items-center justify-between hover:bg-slate-50 cursor-pointer"
                        onClick={() => toggleExpand(item.categoria_id)}
                      >
                        <div className="flex items-center gap-3">
                          <button className="h-5 w-5 flex items-center justify-center text-slate-400">
                            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </button>
                          <div 
                            className="h-3 w-3 rounded-full"
                            style={{ backgroundColor: item.categoria?.color || getBarColor(idx).replace('bg-', '#').replace('-500', '') }}
                          />
                          <div>
                            <p className="font-medium text-slate-800">
                              {item.categoria_nombre || item.categoria?.nombre || 'Sin categoría'}
                            </p>
                            <p className="text-xs text-slate-500">{item.count} movimientos</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-semibold ${
                            filtros.tipo === 'Ingreso' ? 'text-emerald-600' : 
                            filtros.tipo === 'Gasto' ? 'text-red-600' : 'text-slate-800'
                          }`}>
                            {formatMoney(item.total)}
                          </p>
                          <p className="text-xs text-slate-500">{item.porcentaje}%</p>
                        </div>
                      </div>
                      
                      {/* Detalle expandido */}
                      {isExpanded && (
                        <div className="px-4 py-3 bg-slate-50 border-t border-slate-100">
                          <div className="ml-8 space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-500">Categoría:</span>
                              <span className="text-slate-700">{item.categoria?.nombre || 'Sin categoría'}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-500">Tipo:</span>
                              <span className={`font-medium ${
                                item.categoria?.tipo === 'Ingreso' ? 'text-emerald-600' : 'text-red-600'
                              }`}>
                                {item.categoria?.tipo || '-'}
                              </span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-500">N° Movimientos:</span>
                              <span className="text-slate-700">{item.count}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-500">Promedio:</span>
                              <span className="text-slate-700">{formatMoney(item.total / item.count)}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </FinAppLayout>
  );
};

export default FinanzasResumenPage;
